import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import AdminBilingualInput from '@/components/admin/AdminBilingualInput';
import AdminImageUpload from '@/components/admin/AdminImageUpload';
import toast from 'react-hot-toast';
import type { BilingualText } from '@/types';

const AdminArtistEditorPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const artists = useAdminStore((s) => s.artists);
  const addArtist = useAdminStore((s) => s.addArtist);
  const updateArtist = useAdminStore((s) => s.updateArtist);
  const fieldLimits = useAdminStore((s) => s.settings?.fieldLimits);

  const isEdit = id !== undefined && id !== 'new';
  const existing = isEdit ? artists.find((a) => a._id === id) : undefined;

  const [name, setName] = useState<BilingualText>({ en: '', ka: '' });
  const [bio, setBio] = useState<BilingualText>({ en: '', ka: '' });
  const [portrait, setPortrait] = useState('');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (existing) {
      setName(existing.name);
      setBio(existing.bio);
      setPortrait(existing.portrait);
      setIsActive(existing.isActive);
    }
  }, [existing]);

  const handleSave = (): void => {
    try {
      if (!name.en.trim()) {
        toast.error('Artist name (EN) is required');
        return;
      }

      if (isEdit && id) {
        updateArtist(id, { name, bio, portrait, isActive });
        toast.success('Artist updated successfully!');
      } else {
        addArtist({ name, bio, portrait, isActive });
        toast.success('Artist created successfully!');
      }
      navigate('/admin/artists');
    } catch (err) {
      console.error('Failed to save artist:', err);
      toast.error('Failed to save artist. Please try again.');
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/artists')}
            className="w-9 h-9 rounded-xl border border-black/[0.06] dark:border-white/[0.08] flex items-center justify-center text-black/30 dark:text-white/30 hover:text-black dark:hover:text-white hover:border-black/15 dark:hover:border-white/15 transition-all"
          >
            <ArrowLeft size={16} />
          </button>
          <div>
            <h1 className="text-2xl font-syncopate font-bold tracking-tighter">
              {isEdit ? `Edit: ${existing?.name.en ?? 'Artist'}` : 'New Artist'}
            </h1>
            <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.3em] text-black/20 dark:text-white/20">
              {isEdit ? 'Modify artist details' : 'Add a new artist to the gallery'}
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl p-6 space-y-6">
        {/* Name */}
        <AdminBilingualInput
          label="Name"
          value={name}
          onChange={setName}
          placeholder="Artist name"
          maxLength={fieldLimits?.title}
        />

        {/* Bio */}
        <AdminBilingualInput
          label="Bio"
          value={bio}
          onChange={setBio}
          textarea
          placeholder="Short biography"
          maxLength={fieldLimits?.description}
        />

        {/* Portrait with upload */}
        <AdminImageUpload
          label="Portrait"
          value={portrait}
          onChange={setPortrait}
          shape="square"
        />

        {/* Active toggle */}
        <div>
          <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 mb-2">
            Status
          </label>
          <button
            type="button"
            onClick={() => setIsActive((v) => !v)}
            className="flex items-center gap-3"
          >
            <div
              className={`w-10 h-6 rounded-full p-0.5 transition-colors ${
                isActive
                  ? 'bg-green-500'
                  : 'bg-black/10 dark:bg-white/10'
              }`}
            >
              <div
                className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                  isActive ? 'translate-x-4' : 'translate-x-0'
                }`}
              />
            </div>
            <span className="text-[11px] font-bold text-black/60 dark:text-white/60">
              {isActive ? 'Active' : 'Inactive'}
            </span>
          </button>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          className="flex items-center gap-2 px-6 py-3 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.15em] rounded-xl hover:opacity-90 transition-opacity"
        >
          <Save size={14} /> {isEdit ? 'Save Changes' : 'Create Artist'}
        </button>
        <button
          onClick={() => navigate('/admin/artists')}
          className="px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-black/40 dark:text-white/40 hover:text-black dark:hover:text-white transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default AdminArtistEditorPage;
